import state from "../state/state";
import globals from "../constants/env";
import _ from "lodash";
import { toast } from "react-toastify";

import { postMessage } from "../actions/mobileAppCommunicationActions";
import { isMobile } from "../utils/utils";

export function showAlert(message, cb) {
	if (message instanceof Error) {
		console.error("receieved an error, expecting an Object", message);
	}
	showInfo("Info", message, cb);
}

export function dismissAlert() {
	state.select(["appState", "alerts"]).set([]);
}

export function showInfo(title, message, cb) {
	if (globals.config.useFancyAlerts) {
		state.select(["appState", "alerts"]).push({
			id: Date.now() + Math.random(),
			type: "info",
			title,
			message,
			callback: cb
		});

		if (isMobile()) {
			postMessage({
				method: "showAlert",
				title: title,
				message: message
			});
		}
	} else {
		alert(message);
		if (cb) {
			cb();
		}
	}
}

export function showError(title, message, cb) {
	if (globals.config.useFancyAlerts) {
		state.select(["appState", "alerts"]).push({
			id: Date.now() + Math.random(),
			type: "danger",
			title,
			message,
			callback: cb
		});

		if (isMobile()) {
			postMessage({
				method: "showAlert",
				title: title,
				message: message
			});
		}
	} else {
		alert(message);
		if (cb) {
			cb();
		}
	}
}

export function showWarning(title, message, cb) {
	if (globals.config.useFancyAlerts) {
		state.select(["appState", "alerts"]).push({
			id: Date.now() + Math.random(),
			type: "warning",
			title,
			message,
			callback: cb
		});

		if (isMobile()) {
			postMessage({
				method: "showAlert",
				title: title,
				message: message
			});
		}
	} else {
		alert(message);
		if (cb) {
			cb();
		}
	}
}

export function showSuccess(title, message, cb) {
	if (globals.config.useFancyAlerts) {
		state.select(["appState", "alerts"]).push({
			id: Date.now() + Math.random(),
			type: "success",
			title,
			message,
			callback: cb
		});
	} else {
		alert(message);
		if (cb) {
			cb();
		}
	}
}

export function showConfirm(message, title, onConfirm) {
	if (globals.config.useFancyAlerts) {
		state.select(["appState", "alerts"]).push({
			message,
			title,
			onConfirm
		});
	} else {
		if (window.confirm(message)) {
			onConfirm();
		}
	}
}

export function showYesNo(message, title, onYesNo) {
	if (globals.config.useFancyAlerts) {
		state.select(["appState", "alerts"]).push({
			message,
			title,
			onYesNo
		});
	} else {
		if (window.confirm(message)) {
			onYesNo();
		}
	}
}

function markErrorAlertShown(err) {
	if (_.isObject(err)) {
		err.alertShown = true;
	}
}

function getErrorDebugDetails(err) {
	if (!_.isObject(err)) {
		return [];
	}

	const details = [];
	const code = `${_.get(err, "code", "")}`.trim();
	const status = _.get(err, "status") || _.get(err, "statusCode");
	const artifact = `${_.get(err, "saveArtifactLabel", "")}`.trim();
	const key = `${_.get(err, "s3Key", "")}`.trim();
	const requestId = `${_.get(err, "requestId") || _.get(err, "s3RequestId") || ""}`.trim();

	if (code) {
		details.push(`Code: ${code}`);
	}
	if (status) {
		details.push(`Status: ${status}`);
	}
	if (artifact) {
		details.push(`Target: ${artifact}`);
	}
	if (key) {
		details.push(`Path: ${key}`);
	}
	if (requestId) {
		details.push(`Request ID: ${requestId}`);
	}

	return details;
}

function isS3BadDigestError(err) {
	if (!_.isObject(err)) {
		return false;
	}

	const code = `${_.get(err, "code", "")}`.trim();
	const message = `${_.get(err, "message", "")}`.trim();
	return code === "BadDigest" || message.includes("Content-MD5") || message.includes("BadDigest");
}

export function getErrorDisplayMessage(err) {
	if (_.isNil(err)) {
		return "Unknown error.";
	}

	if (_.isString(err)) {
		return err;
	}

	if (!_.isObject(err)) {
		const fallback = `${err}`.trim();
		return fallback || "Unknown error.";
	}

	if (err.code === "NetworkingError") {
		return "Network error. Please check connectivity and try again.";
	}

	if (isS3BadDigestError(err)) {
		const messageLines = [
			"The save upload was rejected by S3 because the request checksum did not match the content received.",
			"This is usually caused by a browser extension, proxy, VPN, antivirus product, or unstable network modifying the upload in transit.",
			"Retry in an incognito window or another browser. If it persists, disable VPN / proxy / security extensions and try again."
		];
		const debugDetails = getErrorDebugDetails(err);
		if (debugDetails.length > 0) {
			messageLines.push(debugDetails.join("\n"));
		}
		return messageLines.join("\n\n");
	}

	const primaryMessage =
		`${_.get(err, "response.body.message") || _.get(err, "response.body.err.message") || _.get(err, "message") || ""}`.trim() ||
		`${err}`.trim();
	const debugDetails = getErrorDebugDetails(err);

	if (debugDetails.length > 0) {
		return `${primaryMessage || "Unknown error."}\n\n${debugDetails.join("\n")}`;
	}

	return primaryMessage || "Unknown error.";
}

export function showError2(title, err) {
	if (arguments.length < 2) {
		showError("Error", getErrorDisplayMessage(title));
		return;
	}

	markErrorAlertShown(err);
	showError(title, getErrorDisplayMessage(err));
}

export function showSuccessToast(message, options = null) {

	let _options = {
		position: "top-right",
		autoClose: 3000,
		hideProgressBar: false,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
	};
	if (options) {
		_options = _.assign({}, _options, options);
	}

	toast.success(message, _options);
}

export function showErrorToast(message, options = null) {

	let _options = {
		position: "top-right",
		autoClose: false,
		hideProgressBar: false,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
	};
	if (options) {
		_options = _.assign({}, _options, options);
	}

	toast.error(message, _options);
}

export function showWarningToast(message, options = null) {

	let _options = {
		position: "top-right",
		autoClose: false,
		hideProgressBar: false,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
	};
	if (options) {
		_options = _.assign({}, _options, options);
	}

	toast.warn(message, _options);
}


export function showInfoToast(message, options = null) {

	let _options = {
		position: "top-right",
		autoClose: false,
		hideProgressBar: false,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
	};
	if (options) {
		_options = _.assign({}, _options, options);
	}

	toast.warn(message, _options);
}

export function showSaveActivityError(e) {
	if (_.get(e, "alertShown")) {
		return;
	}

	const detailMessage = getErrorDisplayMessage(e);
	showError("Save Activity", "There was an issue saving the activity. Sorry for the inconvenience, but you will need to refresh the app before you will be able to save. If you did a lot of work since your last save, you will have the opportunity to restore any lost changes." + (detailMessage ? `\n\nError details: ${detailMessage}` : ""));
}
